import { useState, useEffect, useCallback } from 'react';

export interface UseResizableWidthOptions {
  /** Default width in pixels */
  defaultWidth: number;
  /** Minimum allowed width in pixels */
  minWidth?: number;
  /** Maximum allowed width in pixels */
  maxWidth?: number;
  /** Optional localStorage key for persisting width across sessions */
  storageKey?: string;
}

export interface UseResizableWidthResult {
  /** Current width in pixels */
  width: number;
  /** Function to update width to a new absolute value */
  handleResize: (newWidth: number) => void;
  /** Function to reset width to default */
  resetWidth: () => void;
  /** Function to set width directly */
  setWidth: (width: number) => void;
}

/**
 * Hook for managing resizable panel width with constraints and optional persistence.
 *
 * @example
 * ```tsx
 * const { width, handleResize, resetWidth } = useResizableWidth({
 *   defaultWidth: 425,
 *   minWidth: 320,
 *   maxWidth: 800,
 *   storageKey: 'chatSidebarWidth',
 * });
 * ```
 */
export function useResizableWidth({
  defaultWidth,
  minWidth = 0,
  maxWidth = Infinity,
  storageKey,
}: UseResizableWidthOptions): UseResizableWidthResult {
  // Initialize width from localStorage if available, otherwise use default
  const [width, setWidthState] = useState<number>(() => {
    if (storageKey && typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const parsed = parseInt(stored, 10);
          if (!isNaN(parsed)) {
            // Apply constraints to stored value
            return Math.max(minWidth, Math.min(maxWidth, parsed));
          }
        }
      } catch (error) {
        console.warn(`Failed to load width from localStorage key "${storageKey}":`, error);
      }
    }
    return defaultWidth;
  });

  // Persist to localStorage whenever width changes
  useEffect(() => {
    if (storageKey && typeof window !== 'undefined') {
      try {
        localStorage.setItem(storageKey, width.toString());
      } catch (error) {
        console.warn(`Failed to save width to localStorage key "${storageKey}":`, error);
      }
    }
  }, [width, storageKey]);

  // Handle resize to new absolute width (used during drag)
  const handleResize = useCallback(
    (newWidth: number) => {
      setWidthState(Math.max(minWidth, Math.min(maxWidth, newWidth)));
    },
    [minWidth, maxWidth]
  );

  // Reset to default width
  const resetWidth = useCallback(() => {
    setWidthState(defaultWidth);
  }, [defaultWidth]);

  // Set width directly with constraints applied
  const setWidth = useCallback(
    (newWidth: number) => {
      setWidthState(Math.max(minWidth, Math.min(maxWidth, newWidth)));
    },
    [minWidth, maxWidth]
  );

  return {
    width,
    handleResize,
    resetWidth,
    setWidth,
  };
}
